import { useEffect, useRef, useState } from "react";
import { BaseLogo } from "../../../../core/models/entities/BaseLogo";
import { useOrderActionHandler } from "../hooks/useOrderActionsHandler";
import { CartItemLogo, QuantityRow } from "../models/CartItem";
import { LogoTemplate } from "../../../../core/models/entities/DesignTemplate";
import { useUploadDialog } from "./UploadDialog";
import { LogoQuantitiesColumn, LogoQuantityRow } from "./LogoQuantitiesColumn";
import { Stepper } from "../../../components/Buttons/Stepper";
import {
  CasingOption,
  getCaseLabel,
  transformStringCase,
} from "../../../shared/functions/casing";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { faCircle } from "@fortawesome/free-regular-svg-icons";
import { useTranslation } from "react-i18next";
import { useRepositoriesContext } from "../../../../contexts/RepositoriesContext";

interface LogoQuantitySelectorProps {
  logo: BaseLogo;
  orderLine?: CartItemLogo;
}

interface CasingCheckboxProps {
  casing: CasingOption;
  onChange: (checked: boolean) => void;
  checked: boolean;
}

const CasingCheckbox = ({ casing, checked, onChange }: CasingCheckboxProps) => {
  const toggle = () => {
    onChange(!checked);
  };
  return (
    <div className="flex flex-col items-center">
      <div className="text-sm select-none">{getCaseLabel(casing)}</div>
      <div onClick={toggle} className="cursor-pointer">
        {checked ? (
          <FontAwesomeIcon size="xl" color="#7BBB58" icon={faCheckCircle} />
        ) : (
          <FontAwesomeIcon size="xl" color="#EEEEEE" icon={faCircle} />
        )}
      </div>
    </div>
  );
};

const getEmptyQuantityRow = (
  label: string = "",
  quantity: number = 0
): QuantityRow => ({ label, quantity });
const getEmptyNumericQuantityColumn = (
  existing?: QuantityRow[],
  defaultQuantity: number = 10
) => {
  const standardRows = new Array(10)
    .fill(0)
    .map((_, i) =>
      getEmptyQuantityRow(
        i.toString(),
        existing?.find((e) => e.label === i.toString())?.quantity ??
          defaultQuantity
      )
    );

  const extraRows =
    existing?.filter(
      (e) =>
        !["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"].includes(e.label)
    ) ?? [];

  return [...standardRows, ...extraRows];
};

export function LogoQuantitySelector({
  logo,
  orderLine,
}: LogoQuantitySelectorProps) {
  const { t } = useTranslation();
  const {
    handleLogoSetQuantities,
    handleUploadLogoTemplate,
    updateLogoOrderLine,
  } = useOrderActionHandler();

  const isNumeric = logo.logoType === "NumberLogo";
  const numberOrName = isNumeric ? t("number") : t("name");
  const hasCustomizableQuantity = ["NumberLogo", "NameLogo"].includes(
    logo.logoType
  );

  // Admins can turn off ordering extra logos to storage (stock orders).
  // Shares the cached logos/options/all query with the rest of the app.
  // Fail-open: while the flag is unknown (loading/error) the UI stays as
  // before — the API degrades storage→delivery anyway. Hiding the inputs
  // only prevents NEW storage quantities from being entered; storage values
  // already in the cart are left untouched (the server converts them).
  const { logoRepository } = useRepositoriesContext();
  const options = logoRepository.useOptions();
  const stockOrdersDisabled = options.data?.stockOrdersDisabled === true;

  const toDelivery = orderLine?.toDelivery ?? 0;
  const toStorage = orderLine?.toStorage ?? 0;
  const [logosToStorage, setLogosToStorage] = useState<number>(toStorage);
  const [logosToDelivery, setLogosToDelivery] = useState<number>(toDelivery);
  const [casing, setCasing] = useState<CasingOption>(
    orderLine?.casing ?? CasingOption.UserDefined
  );

  const initialDeliveryRow: QuantityRow = {
    label: orderLine?.value?.toString() ?? "",
    quantity: orderLine?.toDelivery ?? 0,
  };
  const [deliveryRows, setDeliveryRows] = useState<QuantityRow[]>(
    orderLine?.deliveryQuantities ?? [initialDeliveryRow]
  );
  const [storageRows, setStorageRows] = useState<QuantityRow[]>(
    orderLine?.storageQuantities ?? []
  );

  // Sync stepper state when the cart's quantities change externally — e.g.,
  // the price-tier nudge calls handleLogoSetQuantities directly, so the
  // orderLine reference updates but its id doesn't. Skip the sync when the
  // local value is NaN (user is mid-edit on an empty input); the next
  // effect already coerced 0 into the cart so this would otherwise yank an
  // empty field back to "0" while the user is still typing.
  useEffect(() => {
    if (!orderLine) return;
    setLogosToDelivery((prev) =>
      Number.isFinite(prev) ? orderLine.toDelivery ?? 0 : prev,
    );
    setLogosToStorage((prev) =>
      Number.isFinite(prev) ? orderLine.toStorage ?? 0 : prev,
    );
  }, [orderLine?.toDelivery, orderLine?.toStorage]);

  useEffect(() => {
    if (!orderLine) return;
    setLogosToDelivery(orderLine?.toDelivery ?? 0);
    setLogosToStorage(orderLine?.toStorage ?? 0);
    // If the cart already has a casing, adopt it. Otherwise, if the user picked one
    // before the cart entry existed, push the local choice up to the cart so it persists.
    if (orderLine.casing != null) {
      setCasing(orderLine.casing);
    } else if (casing !== CasingOption.UserDefined) {
      updateLogoOrderLine(logo, undefined, undefined, casing);
    }
    if (hasCustomizableQuantity) {
      if (logo.logoType == "NameLogo") {
        // For name logos, if there are no existing delivery quantities,
        // automatically show one empty input field for the user to start typing
        const hasExistingDeliveryQuantities =
          orderLine?.deliveryQuantities &&
          orderLine.deliveryQuantities.length > 0;
        setDeliveryRows(
          hasExistingDeliveryQuantities
            ? orderLine.deliveryQuantities
            : [getEmptyQuantityRow("", 1)]
        );
        setStorageRows(orderLine?.storageQuantities ?? []);
      } else if (logo.logoType == "NumberLogo") {
        const newDeliveryRows = getEmptyNumericQuantityColumn(
          orderLine?.deliveryQuantities,
          10
        );
        const newStorageRows = getEmptyNumericQuantityColumn(
          orderLine?.storageQuantities,
          0
        );

        setDeliveryRows(newDeliveryRows);
        setStorageRows(newStorageRows);

        // Update order line with default quantities for number logos
        // This ensures the cart context has the correct quantities even if user doesn't make changes
        if (orderLine) {
          orderLine.deliveryQuantities = newDeliveryRows;
          orderLine.storageQuantities = newStorageRows;
          updateLogoOrderLine(logo, newDeliveryRows, newStorageRows);
        }
      }
    }
  }, [orderLine?.id]);

  useEffect(() => {
    // if (hasCustomizableQuantity) return
    // Two different approaches to handling quantities on different levels dictate
    //  that we enable the appropriate quantity feature depending on the type of logo.
    // Coerce NaN (a temporarily-empty input on its way to a real number) to 0
    // so the cart never holds NaN — that would silently break price/fee
    // calculations downstream that compare against `> 0`.
    const safeDelivery = Number.isFinite(logosToDelivery) ? logosToDelivery : 0;
    const safeStorage = Number.isFinite(logosToStorage) ? logosToStorage : 0;
    handleLogoSetQuantities(logo, safeDelivery, safeStorage);
    if (orderLine == null) return;
    orderLine.deliveryQuantities = deliveryRows;
    orderLine.storageQuantities = storageRows;
  }, [logosToDelivery, logosToStorage]);

  const handleCasingChange = (newCasing: CasingOption) => {
    setCasing(newCasing);
    if (isNumeric) {
      // For numeric logos, casing is meaningless — still persist the flag for UI consistency.
      updateLogoOrderLine(logo, undefined, undefined, newCasing);
      return;
    }
    const transformedDelivery = deliveryRows.map((row) => ({
      ...row,
      label: transformStringCase(row.label, newCasing),
    }));
    const transformedStorage = storageRows.map((row) => ({
      ...row,
      label: transformStringCase(row.label, newCasing),
    }));
    setDeliveryRows(transformedDelivery);
    setStorageRows(transformedStorage);
    updateLogoOrderLine(logo, transformedDelivery, transformedStorage, newCasing);
  };

  // Update total quantities when delivery/storage rows change for custom quantity logos
  useEffect(() => {
    if (!hasCustomizableQuantity) return;

    const totalDelivery = deliveryRows.reduce(
      (sum, row) => sum + (row.quantity || 0),
      0
    );
    const totalStorage = storageRows.reduce(
      (sum, row) => sum + (row.quantity || 0),
      0
    );

    if (totalDelivery !== logosToDelivery) {
      setLogosToDelivery(totalDelivery);
    }
    if (totalStorage !== logosToStorage) {
      setLogosToStorage(totalStorage);
    }
  }, [
    deliveryRows,
    storageRows,
    hasCustomizableQuantity,
    logosToDelivery,
    logosToStorage,
  ]);

  // Curry
  const getCasingSetter = (casing: CasingOption) => () => handleCasingChange(casing);

  const getUpdateQuantityState =
    (setQuantityState: (quantity: number) => void) => (value: number) =>
      setQuantityState(value);
  const updateRowGeneric = (
    index: number,
    rowState: QuantityRow[],
    setRowState: (rows: QuantityRow[]) => void,
    update: (row: QuantityRow) => void
  ) => {
    const row = rowState[index];
    if (!row) return;
    update(row);
    const updatedRows = [...rowState];
    updatedRows[index] = row;
    // // Remove all rows with 0 or less quantity.
    // const filteredRows = updatedRows.filter((row) => row.quantity > 0)
    setRowState(updatedRows);
    return updatedRows;
  };

  const updateDeliveryRow = (
    index: number,
    update: (row: QuantityRow) => void
  ) => {
    const rows = updateRowGeneric(index, deliveryRows, setDeliveryRows, update);
    updateLogoOrderLine(logo, rows, undefined);
  };

  const updateStorageRow = (
    index: number,
    update: (row: QuantityRow) => void
  ) => {
    const rows = updateRowGeneric(index, storageRows, setStorageRows, update);
    updateLogoOrderLine(logo, undefined, rows);
  };

  const getAddRow =
    (
      rowState: QuantityRow[],
      setRowState: (rows: QuantityRow[]) => void,
      isStorageRow: boolean = false
    ) =>
    () => {
      let defaultQuantity = isNumeric ? 10 : 1;
      // For numeric logos, storage rows should default to 0
      if (isNumeric && isStorageRow) {
        defaultQuantity = 0;
      }
      setRowState([...rowState, getEmptyQuantityRow("", defaultQuantity)]);
    };

  const addStorageRow = getAddRow(storageRows, setStorageRows, true);
  const addDeliveryRow = getAddRow(deliveryRows, setDeliveryRows, false);

  const removeDeliveryRow = (index: number) => {
    let rows = deliveryRows.filter((_, i) => i !== index);
    // Keep one empty input around so the user can continue typing
    // (mirrors the initial state for name logos).
    if (rows.length === 0) rows = [getEmptyQuantityRow("", 1)];
    setDeliveryRows(rows);
    updateLogoOrderLine(logo, rows, undefined);
  };

  // Enter in a name field triggers the same action as the "Add name" button
  // and moves focus to the fresh empty row, so many names can be typed in one flow.
  const deliveryRowsRef = useRef<HTMLDivElement>(null);
  const pendingFocusRef = useRef(false);

  const focusLastNameInput = () => {
    const inputs = deliveryRowsRef.current?.querySelectorAll<HTMLInputElement>(
      'input[type="text"]'
    );
    if (inputs && inputs.length > 0) inputs[inputs.length - 1].focus();
  };

  const handleNameRowEnter = () => {
    const lastRow = deliveryRows[deliveryRows.length - 1];
    if (lastRow && lastRow.label.trim() === "") {
      // An empty row is already waiting — reuse it instead of stacking empty rows.
      focusLastNameInput();
      return;
    }
    pendingFocusRef.current = true;
    addDeliveryRow();
  };

  useEffect(() => {
    if (!pendingFocusRef.current) return;
    pendingFocusRef.current = false;
    focusLastNameInput();
  }, [deliveryRows.length]);

  // Totals for the visible name list, derived from the same state that is
  // sent to the cart, so the summary can never diverge from the actual data.
  const namedDeliveryRows = deliveryRows.filter(
    (row) => row.label.trim() !== ""
  );
  const totalNamedQuantity = namedDeliveryRows.reduce(
    (sum, row) => sum + (Number.isFinite(row.quantity) ? row.quantity : 0),
    0
  );

  const namesSummary = !isNumeric ? (
    <p className="text-xs text-gray-500">
      {t("orderCreate.namesTotalSummary", {
        count: namedDeliveryRows.length,
        qty: totalNamedQuantity,
      })}
    </p>
  ) : undefined;

  // The logo preview image is server-rendered, so it cannot reflect the casing
  // choice client-side. Instead we show a small live text preview of the
  // selected lettering style, applied to the first entered name (or a sample).
  const casingSampleName =
    namedDeliveryRows[0]?.label.trim() || t("orderCreate.letteringSampleName");
  const casingPreview =
    casing === CasingOption.UserDefined
      ? t("orderCreate.letteringAsTyped")
      : transformStringCase(casingSampleName, casing);

  const mapLogoTemplate = (
    template: LogoTemplate,
    setRowState: (rows: QuantityRow[]) => void
  ) => {
    if (!Array.isArray(template.logos)) return;
    else {
      const logos = template.logos.map((item) => ({
        label: item.value,
        quantity: item.quantity,
      }));
      setRowState(logos);
      return logos;
    }
  };

  const orgId = orderLine?.baseLogo?.customer?.id ?? logo?.customer?.id;
  const { component: deliveryDialog, open: openDeliveryDialog } =
    useUploadDialog({
      orgId,
      typeString: "logos",
      onUpload: async (orgId, file) => {
        const output: LogoTemplate = await handleUploadLogoTemplate(
          orgId,
          file
        );
        const logos = mapLogoTemplate(output, setDeliveryRows);
        const baseLogo = orderLine?.baseLogo;
        if (!baseLogo) return;
        updateLogoOrderLine(baseLogo, logos);
      },
    });

  const { component: storageDialog, open: openStorageDialog } = useUploadDialog(
    {
      orgId,
      typeString: "logos",
      onUpload: async (orgId, file) => {
        const output = await handleUploadLogoTemplate(orgId, file);
        const logos = mapLogoTemplate(output, setStorageRows);
        const baseLogo = orderLine?.baseLogo;
        if (!baseLogo) return;
        updateLogoOrderLine(baseLogo, undefined, logos);
      },
    }
  );

  return (
    <>
      {hasCustomizableQuantity && (
        <div>
          <span className="block pt-1 mb-2">
            <p className="block text-sm font-bold uppercase">
              {t("orderCreate.toDelivery")}
            </p>
          </span>
          <LogoQuantitiesColumn
            title={numberOrName}
            actionLabel={t("orderCreate.addLabel", { label: numberOrName })}
            onAction={addDeliveryRow}
            onUpload={openDeliveryDialog}
            summary={namesSummary}
            scrollable={!isNumeric}
            rowsRef={!isNumeric ? deliveryRowsRef : undefined}
          >
            {deliveryRows.map((item, index) => (
              <LogoQuantityRow
                key={index}
                isNumeric={isNumeric}
                label={item.label}
                onLabelChange={(label) =>
                  updateDeliveryRow(index, (row) => {
                    row.label = !isNumeric
                      ? transformStringCase(label, casing)
                      : label;
                  })
                }
                quantity={item.quantity}
                onQuantityChange={(q) =>
                  updateDeliveryRow(index, (row) => {
                    row.quantity = q;
                  })
                }
                placeholder={
                  !isNumeric ? t("orderCreate.enterNamePlaceholder") : undefined
                }
                onEnter={!isNumeric ? handleNameRowEnter : undefined}
                onRemove={!isNumeric ? () => removeDeliveryRow(index) : undefined}
              />
            ))}
          </LogoQuantitiesColumn>

          {!isNumeric && (
            <div className="pt-2">
              <p className="block text-sm font-bold uppercase">
                {t("orderCreate.letteringStyle")}
              </p>
              <div className="flex flex-row flex-1 gap-x-4 pt-2">
                <CasingCheckbox
                  onChange={getCasingSetter(CasingOption.Uppercase)}
                  checked={casing === CasingOption.Uppercase}
                  casing={CasingOption.Uppercase}
                />
                <CasingCheckbox
                  onChange={getCasingSetter(CasingOption.UserDefined)}
                  checked={casing === CasingOption.UserDefined}
                  casing={CasingOption.UserDefined}
                />
                <CasingCheckbox
                  onChange={getCasingSetter(CasingOption.Capitalized)}
                  checked={casing === CasingOption.Capitalized}
                  casing={CasingOption.Capitalized}
                />
                <CasingCheckbox
                  onChange={getCasingSetter(CasingOption.Lowercase)}
                  checked={casing === CasingOption.Lowercase}
                  casing={CasingOption.Lowercase}
                />
              </div>
              <p className="pt-1 text-xs text-gray-500">
                {t("orderCreate.letteringPreviewLabel", {
                  preview: casingPreview,
                })}
              </p>
            </div>
          )}
        </div>
      )}

      {hasCustomizableQuantity && isNumeric && !stockOrdersDisabled && (
        <div>
          <span className="block pt-1 mb-2">
            <p className="block text-sm font-bold uppercase">
              {t("orderCreate.toStorage")}
            </p>
          </span>
          <LogoQuantitiesColumn
            title={numberOrName}
            actionLabel={t("orderCreate.addLabel", { label: numberOrName })}
            onAction={addStorageRow}
            onUpload={openStorageDialog}
          >
            {storageRows.map((item, index) => (
              <LogoQuantityRow
                key={index}
                isNumeric={isNumeric}
                label={item.label}
                onLabelChange={(label) =>
                  updateStorageRow(index, (row) => {
                    row.label = label;
                  })
                }
                quantity={item.quantity}
                onQuantityChange={(q) =>
                  updateStorageRow(index, (row) => {
                    row.quantity = q;
                  })
                }
                placeholder={
                  !isNumeric ? t("orderCreate.enterNamePlaceholder") : undefined
                }
              />
            ))}
          </LogoQuantitiesColumn>
        </div>
      )}

      {!hasCustomizableQuantity && (
        <div>
          <div className={"block text-sm font-bold uppercase"}>
            {t("orderCreate.qty")}
          </div>
          {!stockOrdersDisabled && (
            <>
              <span className="block pt-1 text-xs">
                <p className={"uppercase inline pr-1"}>
                  {t("orderCreate.logosToStorage")}
                </p>
              </span>
              <Stepper
                onValueChange={getUpdateQuantityState(setLogosToStorage)}
                min={0}
                value={logosToStorage}
              />
            </>
          )}
          <span className="block pt-1 text-xs">
            <p className={"uppercase inline pr-1"}>
              {t("orderCreate.logosToDelivery")}
            </p>
          </span>
          <Stepper
            onValueChange={getUpdateQuantityState(setLogosToDelivery)}
            min={0}
            value={logosToDelivery}
          />
        </div>
      )}
      {deliveryDialog}
      {storageDialog}
    </>
  );
}
